import React, { useMemo } from 'react';

import { Drawer } from '@material-ui/core';
import { StyledAvatar } from 'components/avatars';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FlexContainer, StyledTypography } from 'styles/common';

import { CloseIcon2, DustbinSvgIcon } from 'constants/icons';
import theme from 'constants/theme';

import { addEmployeeToCustomDefinition, deleteEmployeeFromCustomDefinition } from 'services/customDefinition';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { Typography13, Typography14 } from 'uiToolkit/Typography';

import { CUSTOM_ROLE_ACTION_TYPES, useCustomRoleContext } from './CustomRoleContext';
import SearchEmployees from './SearchEmployees';

const StyledImage = styled.img`
  max-width: 45%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SelectedEmployeesWrapper = styled.div`
  width: 100%;
  overflow-y: auto;
`;

const StyledDeleteIcon = styled(DustbinSvgIcon)`
  visibility: hidden;
`;

const StyledSelectedEmployee = styled(FlexContainer)`
  height: 66px;
  align-items: center;
  border: 1px solid #eaeaea;
  box-sizing: border-box;
  border-radius: 4px;
  padding-left: 24px;
  padding-right: 24px;

  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
    ${StyledDeleteIcon} {
      visibility: visible;
    }
  }
`;

const SelectedEmployee = ({ profilePhoto, displayName, onRemove }) => (
  <StyledSelectedEmployee>
    <StyledAvatar size="tiny" rightGutter={3} alt={displayName} src={profilePhoto} />
    <FlexContainer style={{ flexGrow: '1' }}>
      <StyledTypography variant="body2" color={theme.palette.text.heading}>
        {displayName.slice(0, 30)}
        {displayName.length > 30 ? '...' : ''}
      </StyledTypography>
    </FlexContainer>
    <StyledDeleteIcon
      bgColor="white"
      onClick={onRemove}
      size="fixed12px"
      containerSize="fixed24px"
      circularOutline
      color="primary"
    />
  </StyledSelectedEmployee>
);

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const Header = ({ onClose }) => (
  <HeaderContainer>
    <Typography13> Add or Edit employees </Typography13>
    <CloseIcon2 onClick={onClose} size="fixed16px" color={theme.palette.icons.grey2} />
  </HeaderContainer>
);

const AddEmployeeSectionContainer = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const AddEmployeeSection = ({ excludeEmpIds, onSelect }) => (
  <AddEmployeeSectionContainer>
    <Typography14 bottomGutter={1.5} color="#666666">
      Add Employees
    </Typography14>
    <SearchEmployees exclusions={excludeEmpIds} onSelect={onSelect} />
  </AddEmployeeSectionContainer>
);

// using this in 1:1 landing page.
export const RoleMemberEditSidePanel = () => {
  const {
    state: { isEmployeeSidePanelOpen, customRoleTableRowObj },
    customRoleDispatch,
  } = useCustomRoleContext();

  const sortedEmployes = useMemo(() => {
    if (Array.isArray(customRoleTableRowObj?.employees))
      return [...customRoleTableRowObj.employees].sort((a, b) => {
        const nameA = a.displayName.toLowerCase();
        const nameB = b.displayName.toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
  }, [customRoleTableRowObj?.employees]);

  const dispatch = useDispatch();
  const excludeEmpIds = customRoleTableRowObj?.employees?.map((emp) => emp.id);

  const handleClose = () => {
    customRoleDispatch({
      type: CUSTOM_ROLE_ACTION_TYPES.closeEmployeeEditSidePanel,
      payload: null,
    });
  };

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) handleClose();
  };

  const onSelect = (employee) => {
    const employeesIndex = sortedEmployes.findIndex((emp) => emp.id === employee.id);

    if (employeesIndex === -1) {
      addEmployeeToCustomDefinition({
        customDefinitionId: customRoleTableRowObj.customRole.id,
        empIds: [employee.id],
      })
        .then((res) => {
          dispatch(showSuccessSnackbar(`${employee.displayName} added successfully.`, 2000));
          customRoleDispatch({
            type: CUSTOM_ROLE_ACTION_TYPES.addEmployeesToRole,
            payload: { employee },
          });
        })
        .catch((err) => {
          console.error(err);
          dispatch(showErrorSnackbar(err.message || `failed to add ${employee.displayName}`));
        });
    }
  };

  const removeSelection = (employee) => {
    deleteEmployeeFromCustomDefinition({
      customDefinitionId: customRoleTableRowObj.customRole.id,
      empId: employee.id,
    })
      .then((res) => {
        dispatch(showSuccessSnackbar(`${employee.displayName} removed successfully.`, 2000));
        customRoleDispatch({
          type: CUSTOM_ROLE_ACTION_TYPES.deleteEmployeesFromRole,
          payload: { id: employee.id },
        });
      })
      .catch((err) => {
        console.error(err);
        dispatch(showErrorSnackbar(err.message || `failed to remove ${employee.displayName}.`));
      });
  };

  return (
    <StyledDrawer
      ModalProps={{
        onBackdropClick: () => {
          handleClose();
        },
      }}
      anchor="right"
      open={isEmployeeSidePanelOpen}
      onKeyDown={handleKeyDown}
    >
      {isEmployeeSidePanelOpen && (
        <>
          <Header onClose={handleClose} />
          <AddEmployeeSection excludeEmpIds={excludeEmpIds} onSelect={onSelect} />
          <ContentWrapper>
            <SelectedEmployeesWrapper>
              {sortedEmployes.map((emp, index) => (
                <SelectedEmployee key={`${emp.id}-${index}`} {...emp} onRemove={() => removeSelection(emp)} />
              ))}
            </SelectedEmployeesWrapper>
          </ContentWrapper>
        </>
      )}
    </StyledDrawer>
  );
};

const StyledDrawer = styled(Drawer)`
  position: relative;
  .MuiDrawer-paper {
    width: 40%;
  }
`;

export default RoleMemberEditSidePanel;
